import { Fragment, type PropsWithChildren } from 'react';
import { Helmet } from 'react-helmet-async';

import { AdaLogo } from '~/components/AdaLogo';
import { WmgLogo } from '~/components/WmgLogo';
import { env } from '~/lib/env';
import { BrandContext } from '~/modules/branding';
import { AdaOktaConfig } from '~/modules/branding/auth/ada-okta-config.ts';
import { WmgOktaConfig } from '~/modules/branding/auth/wmg-okta-config.ts';
import { defaultBrandContext } from '~/modules/branding/contexts/defaultBrandContext';
import type { Brand } from '~/modules/branding/types';
import AdaThemeCssInline from '~/styles/theme-ada.css?inline';
import WmgThemeCssInline from '~/styles/theme-wmg.css?inline';

import type { BrandType } from '../brands';

export type BrandProviderProps = {
  localBrand?: BrandType;
  hostname: string;
};

export function BrandProvider({
  children,
  localBrand,
  hostname,
}: PropsWithChildren<BrandProviderProps>) {
  const brand: Brand = defaultBrandContext;

  const wmgOneDomains = [
    'wmg1.com',
    'staging.wmg1.dev',
    'dev.wmg1.dev',
    localBrand === 'WMG' && 'localhost',
  ];
  const adaHubDomains = [
    'partners.ada-music.com',
    'staging.partners.ada-music.com',
    'dev.partners.ada-music.com',
    localBrand === 'ADA' && 'localhost',
  ];

  if (wmgOneDomains.includes(hostname)) {
    brand.name = 'WMG';
    brand.product = 'WMG1';
    brand.title = 'WMG One';
    brand.titleTemplate = `%s | ${brand.title}`;
    brand.oktaConfig = WmgOktaConfig;
    brand.pulseInsightsToken =
      env('VITE_WMG_PULSE_INSIGHTS_TOKEN', false) || '';
    brand.meta = (
      <Helmet>
        <style>{`${WmgThemeCssInline}`}</style>
      </Helmet>
    );
    brand.logo = <WmgLogo />;
  } else if (adaHubDomains.includes(hostname)) {
    brand.name = 'ADA';
    brand.product = 'ADA';
    brand.title = 'ADA';
    brand.oktaConfig = AdaOktaConfig;
    brand.pulseInsightsToken =
      env('VITE_ADA_PULSE_INSIGHTS_TOKEN', false) || '';
    brand.meta = (
      <Helmet>
        <style>{`${AdaThemeCssInline}`}</style>
      </Helmet>
    );
    brand.logo = <AdaLogo className="text-primary-default" />;
  } else {
    // TODO WMG1-308 - this case shouldn't exist, if we can’t determine a brand, we should either assume WMG or throw an error and stop executing things
    brand.name = null;
    brand.title = '';
    brand.meta = <Fragment />;
  }

  return (
    <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
  );
}

BrandProvider.displayName = 'BrandProvider';
